import { Capacitor } from '@capacitor/core';
import {
  API_BASE_URL,
  APPLE_NATIVE_CLIENT_ID,
  APPLE_NATIVE_REDIRECT_URI,
  GOOGLE_NATIVE_ANDROID_CLIENT_ID,
  GOOGLE_NATIVE_CLIENT_ID,
  GOOGLE_NATIVE_IOS_CLIENT_ID,
  GOOGLE_NATIVE_SERVER_CLIENT_ID,
  NATIVE_OAUTH_REDIRECT_SCHEME,
} from '../config';

export type AuthAccount = {
  id: string;
  email: string;
  displayName: string;
  provider: 'email' | 'google' | 'apple';
};

export type AccessState = {
  premium: boolean;
  freePlay: boolean;
  canPlayMultiplayer: boolean;
};

export type AuthState = {
  token: string;
  account: AuthAccount;
  access: AccessState;
};

export type AccountProfile = {
  account: AuthAccount;
  access: AccessState;
  createdAt: string;
  lastLoginAt: string | null;
  premiumSource: string | null;
  premiumReference: string | null;
  premiumUpdatedAt: string | null;
};

export type AppleIapVerifyPayload = {
  transactionId: string;
  originalTransactionId?: string;
  productId?: string;
};

export type GoogleIapVerifyPayload = {
  purchaseToken: string;
  productId?: string;
  packageName?: string;
  orderId?: string;
  receipt?: string;
  signature?: string;
};

type AppleLoginProfile = {
  email?: string;
  displayName?: string;
};

type AuthApiResponse = {
  success: boolean;
  token?: string;
  account?: AuthAccount;
  access?: AccessState;
  error?: string;
};

type OAuthProvider = 'google' | 'apple';

const TOKEN_KEY = 'pickitup_auth_token';
const AUTH_TOKEN_EVENT = 'pickitup-auth-token';
const AUTH_ERROR_EVENT = 'pickitup-auth-oauth-error';

class AuthManager {
  private static instance: AuthManager;
  private token = '';
  private state: Omit<AuthState, 'token'> | null = null;
  private googleNativeReady = false;
  private googleScriptLoading: Promise<void> | null = null;
  private nativeOAuthListenerReady = false;
  private nativeOAuthListenerLoading: Promise<void> | null = null;
  private lastHandledNativeOAuthUrl = '';

  private constructor() {
    try {
      this.token = localStorage.getItem(TOKEN_KEY) || '';
    } catch {
      this.token = '';
    }
    void this.ensureNativeOAuthListener();
  }

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  getToken(): string {
    return this.token;
  }

  getState(): Omit<AuthState, 'token'> | null {
    return this.state;
  }

  isLoggedIn(): boolean {
    return Boolean(this.token);
  }

  setToken(token: string): void {
    this.token = (token || '').trim();
    try {
      if (this.token) {
        localStorage.setItem(TOKEN_KEY, this.token);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch {
      // ignore
    }
  }

  logout(): void {
    this.setToken('');
    this.state = null;
  }

  async registerEmail(email: string, password: string, displayName: string): Promise<AuthState> {
    const data = await this.post<AuthApiResponse>('/auth/register', { email, password, displayName });
    return this.consumeAuthResponse(data);
  }

  async loginEmail(email: string, password: string): Promise<AuthState> {
    const data = await this.post<AuthApiResponse>('/auth/login', { email, password });
    return this.consumeAuthResponse(data);
  }

  async loginGoogleIdToken(idToken: string): Promise<AuthState> {
    const data = await this.post<AuthApiResponse>('/auth/oauth/google/id-token', { idToken });
    return this.consumeAuthResponse(data);
  }

  async loginGoogleAccessToken(accessToken: string): Promise<AuthState> {
    const data = await this.post<AuthApiResponse>('/auth/oauth/google/access-token', { accessToken });
    return this.consumeAuthResponse(data);
  }

  async loginAppleIdToken(idToken: string, profile?: AppleLoginProfile): Promise<AuthState> {
    const data = await this.post<AuthApiResponse>('/auth/oauth/apple/id-token', {
      idToken,
      email: profile?.email,
      displayName: profile?.displayName,
    });
    return this.consumeAuthResponse(data);
  }

  async loginWithOAuth(provider: OAuthProvider, returnTo: string): Promise<AuthState> {
    if (provider === 'apple' && this.isNativeIOSApp()) {
      return this.loginAppleNative();
    }
    if (provider === 'apple' && (this.isNativeMobileApp() || this.isLikelyNativeMobileShell())) {
      return this.loginWithNativeOAuthRedirect(provider, returnTo);
    }
    if (provider === 'google') {
      if (this.isNativeMobileApp() || this.isLikelyNativeMobileShell()) {
        if (this.isNativeIOSApp()) {
          const clientId = this.getGoogleNativeClientId();
          if (clientId && GOOGLE_NATIVE_SERVER_CLIENT_ID) {
            return this.loginGoogleNative();
          }
        }
        return this.loginWithNativeOAuthRedirect(provider, returnTo);
      }
      if (this.isElectronDesktopApp()) {
        return this.openOAuthPopup('google', returnTo);
      }
      return this.loginGoogleWeb();
    }
    return this.openOAuthPopup(provider, returnTo);
  }

  async refreshMe(): Promise<Omit<AuthState, 'token'>> {
    if (!this.token) throw new Error('Nicht angemeldet.');
    const data = await this.get<AuthApiResponse>('/auth/me');
    if (!data.success || !data.account || !data.access) {
      throw new Error(data.error || 'Account-Status konnte nicht geladen werden.');
    }
    this.state = {
      account: data.account,
      access: data.access,
    };
    return this.state;
  }

  async getProfile(): Promise<AccountProfile> {
    if (!this.token) throw new Error('Nicht angemeldet.');
    const data = await this.get<{ success: boolean; profile?: AccountProfile; error?: string }>('/auth/profile');
    if (!data.success || !data.profile) {
      throw new Error(data.error || 'Profil konnte nicht geladen werden.');
    }
    return data.profile;
  }

  async deleteAccount(confirmText: string): Promise<void> {
    if (!this.token) throw new Error('Nicht angemeldet.');
    const data = await this.del<{ success: boolean; deleted?: boolean; error?: string }>('/auth/account', { confirmText });
    if (!data.success) {
      throw new Error(data.error || 'Account konnte nicht gelöscht werden.');
    }
    this.logout();
  }

  async createStripeCheckout(
    returnTo: string,
    country?: string | null,
  ): Promise<{ checkoutUrl: string; sessionId: string }> {
    const data = await this.post<{ success: boolean; checkoutUrl?: string; sessionId?: string; error?: string }>(
      '/billing/checkout/stripe',
      { returnTo, country },
    );
    const checkoutUrl = String(data.checkoutUrl || '').trim();
    const sessionId = String(data.sessionId || '').trim();
    if (!data.success || !checkoutUrl || !sessionId) {
      throw new Error(data.error || 'Stripe Checkout fehlgeschlagen.');
    }
    return { checkoutUrl, sessionId };
  }

  async createPayPalCheckout(
    returnTo: string,
    country?: string | null,
  ): Promise<{ checkoutUrl: string; orderId: string }> {
    const data = await this.post<{ success: boolean; checkoutUrl?: string; orderId?: string; error?: string }>(
      '/billing/checkout/paypal',
      { returnTo, country },
    );
    const checkoutUrl = String(data.checkoutUrl || '').trim();
    const orderId = String(data.orderId || '').trim();
    if (!data.success || !checkoutUrl || !orderId) {
      throw new Error(data.error || 'PayPal Checkout fehlgeschlagen.');
    }
    return { checkoutUrl, orderId };
  }

  async confirmStripeCheckout(sessionId: string): Promise<AccessState> {
    const data = await this.post<{ success: boolean; access?: AccessState; error?: string }>(
      '/billing/confirm/stripe',
      { sessionId },
    );
    if (!data.success || !data.access) {
      throw new Error(data.error || 'Stripe Bestätigung fehlgeschlagen.');
    }
    if (this.state) {
      this.state = { ...this.state, access: data.access };
    }
    return data.access;
  }

  async capturePayPal(orderId: string): Promise<AccessState> {
    const data = await this.post<{ success: boolean; access?: AccessState; error?: string }>(
      '/billing/paypal/capture',
      { orderId },
    );
    if (!data.success || !data.access) {
      throw new Error(data.error || 'PayPal Capture fehlgeschlagen.');
    }
    if (this.state) {
      this.state = { ...this.state, access: data.access };
    }
    return data.access;
  }

  async verifyAppleIapPurchase(payload: AppleIapVerifyPayload): Promise<AccessState> {
    const data = await this.post<{ success: boolean; access?: AccessState; error?: string }>(
      '/billing/verify/apple-iap',
      payload,
    );
    if (!data.success || !data.access) {
      throw new Error(data.error || 'Apple In-App-Kauf konnte nicht bestätigt werden.');
    }
    if (this.state) {
      this.state = { ...this.state, access: data.access };
    }
    return data.access;
  }

  async verifyGoogleIapPurchase(payload: GoogleIapVerifyPayload): Promise<AccessState> {
    const data = await this.post<{ success: boolean; access?: AccessState; error?: string }>(
      '/billing/verify/google-iap',
      payload,
    );
    if (!data.success || !data.access) {
      throw new Error(data.error || 'Google In-App-Kauf konnte nicht bestätigt werden.');
    }
    if (this.state) {
      this.state = { ...this.state, access: data.access };
    }
    return data.access;
  }

  async openOAuthPopup(provider: OAuthProvider, returnTo: string): Promise<AuthState> {
    const oauthUrl = `${API_BASE_URL}/auth/oauth/${provider}/start?popup=1&returnTo=${encodeURIComponent(returnTo)}`;
    const popup = window.open(oauthUrl, `pickitup-${provider}-auth`, 'width=520,height=720');
    if (!popup) throw new Error('Popup blockiert. Bitte Popup erlauben.');
    const allowedOrigin = new URL(API_BASE_URL).origin;

    return new Promise<AuthState>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('OAuth Timeout'));
      }, 120_000);

      const poll = window.setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('Login-Fenster geschlossen.'));
        }
      }, 450);

      const messageHandler = (event: MessageEvent) => {
        if (event.source !== popup) return;
        if (event.origin !== allowedOrigin) return;
        const data = event.data as any;
        if (!data || typeof data !== 'object') return;
        if (data.type === 'pickitup-auth-error') {
          cleanup();
          reject(new Error(String(data.message || 'OAuth Login fehlgeschlagen.')));
          return;
        }
        if (data.type === 'pickitup-auth-success' && typeof data.token === 'string') {
          cleanup();
          try {
            const result = this.consumeAuthResponse({
              success: true,
              token: data.token,
              account: data.account,
              access: data.access,
            });
            resolve(result);
          } catch (err) {
            reject(err instanceof Error ? err : new Error('OAuth Login fehlgeschlagen.'));
          }
        }
      };

      const cleanup = () => {
        window.clearTimeout(timeout);
        window.clearInterval(poll);
        window.removeEventListener('message', messageHandler);
      };

      window.addEventListener('message', messageHandler);
    });
  }

  private async loginWithNativeOAuthRedirect(provider: OAuthProvider, returnTo: string): Promise<AuthState> {
    await this.ensureNativeOAuthListener();
    if (!this.nativeOAuthListenerReady) {
      throw new Error('Native OAuth Redirect ist nicht verfügbar.');
    }

    const callbackUrl = this.getNativeOAuthCallbackUrl(returnTo);
    const oauthUrl = `${API_BASE_URL}/auth/oauth/${provider}/start?popup=0&returnTo=${encodeURIComponent(callbackUrl)}`;

    return new Promise<AuthState>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error('OAuth Timeout'));
      }, 120_000);

      const tokenHandler = async (event: Event) => {
        const custom = event as CustomEvent<{ token?: string }>;
        const token = String(custom.detail?.token || '').trim();
        if (!token) return;

        cleanup();
        void this.closeNativeOAuthBrowser();
        try {
          this.setToken(token);
          const state = await this.refreshMe();
          resolve({
            token: this.token,
            account: state.account,
            access: state.access,
          });
        } catch (err) {
          reject(err instanceof Error ? err : new Error('OAuth Login fehlgeschlagen.'));
        }
      };

      const errorHandler = (event: Event) => {
        const custom = event as CustomEvent<{ message?: string }>;
        const message = String(custom.detail?.message || '').trim();
        cleanup();
        void this.closeNativeOAuthBrowser();
        reject(new Error(message || 'OAuth Login fehlgeschlagen.'));
      };

      const cleanup = () => {
        window.clearTimeout(timeout);
        window.removeEventListener(AUTH_TOKEN_EVENT, tokenHandler as EventListener);
        window.removeEventListener(AUTH_ERROR_EVENT, errorHandler as EventListener);
      };

      window.addEventListener(AUTH_TOKEN_EVENT, tokenHandler as EventListener);
      window.addEventListener(AUTH_ERROR_EVENT, errorHandler as EventListener);

      void this.openNativeOAuthUrl(oauthUrl).catch((err) => {
        cleanup();
        reject(err instanceof Error ? err : new Error('OAuth Login konnte nicht gestartet werden.'));
      });
    });
  }

  private async loginAppleNative(): Promise<AuthState> {
    if (!APPLE_NATIVE_CLIENT_ID || !APPLE_NATIVE_REDIRECT_URI) {
      throw new Error('Apple Login ist nicht konfiguriert.');
    }

    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
      const response = await SignInWithApple.authorize({
        clientId: APPLE_NATIVE_CLIENT_ID,
        redirectURI: APPLE_NATIVE_REDIRECT_URI,
        scopes: 'email name',
        state: this.createRandomValue(),
        nonce: this.createRandomValue(),
      });
      const appleIdentity = response?.response || (response as Record<string, any> | undefined) || {};
      const idToken = String(appleIdentity?.identityToken || '').trim();
      if (!idToken) {
        throw new Error('Apple ID Token fehlt.');
      }
      const email = String(appleIdentity?.email || '').trim().toLowerCase();
      const givenName = String(appleIdentity?.givenName || '').trim();
      const familyName = String(appleIdentity?.familyName || '').trim();
      const displayName = [givenName, familyName].filter(Boolean).join(' ').trim();
      return this.loginAppleIdToken(idToken, {
        email: email || undefined,
        displayName: displayName || undefined,
      });
    } catch (err) {
      const raw = this.formatErrorMessage(err);
      if (/cancell?ed|abgebrochen|authorizationerror\.canceled|1001/i.test(raw)) {
        throw new Error('Apple Login abgebrochen.');
      }
      if (/not supported|unsupported/i.test(raw)) {
        throw new Error('Apple Login wird auf diesem Gerät nicht unterstützt.');
      }
      throw new Error(raw || 'Apple Login fehlgeschlagen.');
    }
  }

  private async loginGoogleNative(): Promise<AuthState> {
    const clientId = this.getGoogleNativeClientId();
    if (!clientId || !GOOGLE_NATIVE_SERVER_CLIENT_ID) {
      const platform = Capacitor.getPlatform() === 'ios' ? 'iOS' : 'Android';
      throw new Error(
        `Google Login nicht konfiguriert: VITE_GOOGLE_NATIVE_${platform === 'iOS' ? 'IOS' : 'ANDROID'}_CLIENT_ID fehlt.`,
      );
    }

    try {
      const { GoogleAuth } = await import('@southdevs/capacitor-google-auth');
      if (!this.googleNativeReady) {
        await GoogleAuth.initialize({
          clientId,
          scopes: ['profile', 'email', 'openid'],
          grantOfflineAccess: true,
        });
        this.googleNativeReady = true;
      }

      const user = await GoogleAuth.signIn({
        clientId,
        serverClientId: GOOGLE_NATIVE_SERVER_CLIENT_ID,
        scopes: ['profile', 'email', 'openid'],
        grantOfflineAccess: true,
      });

      const idToken = String(user?.authentication?.idToken || '').trim();
      if (idToken) {
        return this.loginGoogleIdToken(idToken);
      }
      const accessToken = String(user?.authentication?.accessToken || '').trim();
      if (accessToken) {
        return this.loginGoogleAccessToken(accessToken);
      }
      throw new Error('Google Token fehlt.');
    } catch (err) {
      const raw = this.formatErrorMessage(err);
      if (/cancell?ed|abgebrochen|12501/i.test(raw)) {
        throw new Error('Google Login abgebrochen.');
      }
      if (/initialize|not ready|google services are not ready/i.test(raw)) {
        throw new Error('Google Login ist nicht initialisiert. Bitte App neu starten.');
      }
      if (/custom scheme uris are not allowed for \"web\" type client/i.test(raw)) {
        throw new Error(
          'Google native Login falsch konfiguriert: Für iOS/Android musst du je einen nativen OAuth-Client verwenden, nicht den WEB-Client.',
        );
      }
      throw new Error(raw || 'Google Login fehlgeschlagen.');
    }
  }

  private async loginGoogleWeb(): Promise<AuthState> {
    if (!GOOGLE_NATIVE_SERVER_CLIENT_ID) {
      throw new Error('Google Web Login ist nicht konfiguriert: VITE_GOOGLE_NATIVE_SERVER_CLIENT_ID fehlt.');
    }
    try {
      const accessToken = await this.requestGoogleBrowserAccessToken();
      return this.loginGoogleAccessToken(accessToken);
    } catch (err) {
      const message = this.formatErrorMessage(err);
      if (/popup|geschlossen|abgebrochen|cancel|not available|nicht verfügbar/i.test(message)) {
        throw new Error(message || 'Google Login abgebrochen.');
      }
      throw new Error(message || 'Google Login fehlgeschlagen.');
    }
  }

  private async ensureNativeOAuthListener(): Promise<void> {
    if (!(this.isNativeMobileApp() || this.isLikelyNativeMobileShell())) return;
    if (this.nativeOAuthListenerReady) return;
    if (this.nativeOAuthListenerLoading) return this.nativeOAuthListenerLoading;

    this.nativeOAuthListenerLoading = (async () => {
      try {
        const { App } = await import('@capacitor/app');
        await App.addListener('appUrlOpen', (event) => {
          this.handleNativeOAuthUrl(String(event?.url || '').trim());
        });

        const launchUrl = await App.getLaunchUrl();
        this.handleNativeOAuthUrl(String(launchUrl?.url || '').trim());
        this.nativeOAuthListenerReady = true;
      } catch {
        this.nativeOAuthListenerReady = false;
      } finally {
        this.nativeOAuthListenerLoading = null;
      }
    })();

    return this.nativeOAuthListenerLoading;
  }

  private getNativeOAuthCallbackUrl(returnTo: string): string {
    if (!(this.isNativeMobileApp() || this.isLikelyNativeMobileShell())) {
      return returnTo;
    }
    const scheme = String(NATIVE_OAUTH_REDIRECT_SCHEME || '').trim().replace(/:$/, '');
    if (!scheme) return returnTo;
    return `${scheme}://oauth/callback`;
  }

  private handleNativeOAuthUrl(urlRaw: string): void {
    const url = String(urlRaw || '').trim();
    if (!url || url === this.lastHandledNativeOAuthUrl) return;
    const parsed = this.extractOAuthResult(url);
    if (!parsed.token && !parsed.error) return;

    this.lastHandledNativeOAuthUrl = url;
    if (parsed.token) {
      this.setToken(parsed.token);
      this.emitOAuthToken(parsed.token);
      return;
    }
    this.emitOAuthError(parsed.error);
  }

  private extractOAuthResult(urlRaw: string): { token: string; error: string } {
    try {
      const url = new URL(urlRaw);
      const token = String(url.searchParams.get('authToken') || '').trim();
      const error = String(url.searchParams.get('error') || url.searchParams.get('authError') || '').trim();
      return { token, error };
    } catch {
      return { token: '', error: '' };
    }
  }

  private emitOAuthToken(token: string): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(AUTH_TOKEN_EVENT, { detail: { token } }));
  }

  private emitOAuthError(message: string): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { message } }));
  }

  private async openNativeOAuthUrl(url: string): Promise<void> {
    if (!(this.isNativeMobileApp() || this.isLikelyNativeMobileShell())) {
      window.location.assign(url);
      return;
    }

    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    } catch {
      window.location.assign(url);
    }
  }

  private async closeNativeOAuthBrowser(): Promise<void> {
    if (!(this.isNativeMobileApp() || this.isLikelyNativeMobileShell())) return;
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.close();
    } catch {
      // ignore
    }
  }

  private async requestGoogleBrowserAccessToken(): Promise<string> {
    await this.ensureGoogleIdentityScript();
    const googleApi = (window as Window & {
      google?: {
        accounts?: {
          oauth2?: {
            initTokenClient: (config: {
              client_id: string;
              scope: string;
              prompt?: string;
              callback: (response: { access_token?: string; error?: string; error_description?: string }) => void;
              error_callback?: (error: { type?: string; message?: string }) => void;
            }) => { requestAccessToken: (opts?: { prompt?: string }) => void };
          };
        };
      };
    }).google;

    const oauth2 = googleApi?.accounts?.oauth2;
    if (!oauth2) {
      throw new Error('Google Web SDK nicht verfügbar.');
    }

    return new Promise<string>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error('Google Login Timeout.'));
      }, 120_000);

      const finish = (fn: () => void) => {
        window.clearTimeout(timeout);
        fn();
      };

      const tokenClient = oauth2.initTokenClient({
        client_id: GOOGLE_NATIVE_SERVER_CLIENT_ID,
        scope: 'openid email profile',
        prompt: 'select_account',
        callback: (response) => {
          if (response.error) {
            finish(() => reject(new Error(response.error_description || response.error || 'Google Login fehlgeschlagen.')));
            return;
          }
          const accessToken = String(response.access_token || '').trim();
          if (!accessToken) {
            finish(() => reject(new Error('Google Access Token fehlt.')));
            return;
          }
          finish(() => resolve(accessToken));
        },
        error_callback: (error) => {
          finish(() => reject(new Error(error?.message || error?.type || 'Google Login Popup fehlgeschlagen.')));
        },
      });

      try {
        tokenClient.requestAccessToken({ prompt: 'select_account' });
      } catch (err) {
        finish(() => reject(err instanceof Error ? err : new Error('Google Login konnte nicht gestartet werden.')));
      }
    });
  }

  private async ensureGoogleIdentityScript(): Promise<void> {
    const maybeGoogle = (window as Window & { google?: unknown }).google;
    if (maybeGoogle) return;
    if (this.googleScriptLoading) return this.googleScriptLoading;

    this.googleScriptLoading = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-pickitup-google-gsi="1"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Google Web SDK konnte nicht geladen werden.')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset.pickitupGoogleGsi = '1';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Web SDK konnte nicht geladen werden.'));
      document.head.appendChild(script);
    });

    try {
      await this.googleScriptLoading;
    } finally {
      this.googleScriptLoading = null;
    }
  }

  private isNativeIOSApp(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  }

  private isNativeMobileApp(): boolean {
    if (!Capacitor.isNativePlatform()) return false;
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  }

  private isLikelyNativeMobileShell(): boolean {
    if (typeof window === 'undefined') return false;
    const protocol = String(window.location.protocol || '').toLowerCase();
    const isCapacitorProtocol = protocol === 'capacitor:' || protocol === 'ionic:';
    if (!isCapacitorProtocol) return false;
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android' || platform === 'web';
  }

  private isElectronDesktopApp(): boolean {
    if (typeof window === 'undefined') return false;
    const userAgent = String(typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
    if (userAgent.includes('electron')) return true;
    const desktop = (window as Window & {
      pickitupDesktop?: { versions?: { electron?: string } };
    }).pickitupDesktop;
    return Boolean(desktop?.versions?.electron);
  }

  private getGoogleNativeClientId(): string {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return GOOGLE_NATIVE_IOS_CLIENT_ID || GOOGLE_NATIVE_CLIENT_ID;
    if (platform === 'android') return GOOGLE_NATIVE_ANDROID_CLIENT_ID || GOOGLE_NATIVE_CLIENT_ID;
    return GOOGLE_NATIVE_CLIENT_ID;
  }

  private createRandomValue(length = 24): string {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  private consumeAuthResponse(data: AuthApiResponse): AuthState {
    if (!data.success || !data.token || !data.account || !data.access) {
      throw new Error(data.error || 'Anmeldung fehlgeschlagen.');
    }
    this.setToken(data.token);
    this.state = {
      account: data.account,
      access: data.access,
    };
    return {
      token: data.token,
      account: data.account,
      access: data.access,
    };
  }

  private async get<T>(path: string): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'GET',
        headers: this.authHeaders(),
      });
    } catch (err) {
      throw new Error(this.normalizeNetworkError(err));
    }
    const data = await response.json().catch(() => ({ success: false, error: 'Ungültige Serverantwort.' }));
    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `HTTP ${response.status}`);
    }
    return data as T;
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        headers: {
          ...this.authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body ?? {}),
      });
    } catch (err) {
      throw new Error(this.normalizeNetworkError(err));
    }
    const data = await response.json().catch(() => ({ success: false, error: 'Ungültige Serverantwort.' }));
    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `HTTP ${response.status}`);
    }
    return data as T;
  }

  private async del<T>(path: string, body: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        method: 'DELETE',
        headers: {
          ...this.authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body ?? {}),
      });
    } catch (err) {
      throw new Error(this.normalizeNetworkError(err));
    }
    const data = await response.json().catch(() => ({ success: false, error: 'Ungültige Serverantwort.' }));
    if (!response.ok) {
      throw new Error((data as { error?: string }).error || `HTTP ${response.status}`);
    }
    return data as T;
  }

  private authHeaders(): Record<string, string> {
    if (!this.token) return {};
    return { Authorization: `Bearer ${this.token}` };
  }

  private formatErrorMessage(err: unknown): string {
    return (err instanceof Error ? err.message : String(err || '')).trim();
  }

  private normalizeNetworkError(err: unknown): string {
    const message = this.formatErrorMessage(err).toLowerCase();
    if (!message) return 'Netzwerkfehler. Server nicht erreichbar.';
    if (message.includes('load failed') || message.includes('failed to fetch') || message.includes('network request failed')) {
      return 'Netzwerk/CORS-Fehler: Server konnte nicht erreicht werden.';
    }
    return this.formatErrorMessage(err);
  }
}

export default AuthManager;
